import {
  Modal,
  ModalContent,
  ModalBody,
  Button,
} from '@heroui/react';
import { Download, FolderInput, Trash2, Eye, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient } from '@/api/client';
import { useDeleteKnowledge } from '@/api/knowledge';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { useMobileRole } from '@/hooks/useMobileRole';

interface FileInfo {
  id: string;
  title: string;
  mime_type: string;
  file_size: number | null;
  created_at: string;
}

interface MobileFileActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  file: FileInfo | null;
  projectId: string;
  onMoveToFolder?: () => void;
  onOpenViewer?: (fileId: string) => void;
}

export default function MobileFileActionSheet({
  isOpen,
  onClose,
  file,
  projectId,
  onMoveToFolder,
  onOpenViewer,
}: MobileFileActionSheetProps) {
  const { isTablet } = useBreakpoint();
  const { canViewMarkup } = useMobileRole();
  const deleteMutation = useDeleteKnowledge(projectId);

  if (!file) return null;

  const handleDownload = async () => {
    try {
      const { data } = await apiClient.get<{ url: string }>(
        `/knowledge/${file.id}/signed-url`,
      );
      window.open(data.url, '_blank');
      onClose();
    } catch {
      toast.error('Failed to get download URL');
    }
  };

  const handleDelete = () => {
    if (!confirm(`Delete "${file.title}"?`)) return;
    deleteMutation.mutate(file.id, {
      onSuccess: () => {
        toast.success('File deleted');
        onClose();
      },
      onError: (err) => toast.error(`Delete failed: ${err.message}`),
    });
  };

  const formatSize = (bytes: number | null) => {
    if (!bytes) return '';
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const ext = file.title.split('.').pop()?.toUpperCase() ?? '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      placement="bottom"
      size="full"
      classNames={{
        base: 'rounded-t-xl m-0 sm:m-0',
        body: 'p-0',
      }}
      style={{ maxHeight: '60vh' }}
      hideCloseButton
    >
      <ModalContent style={{ backgroundColor: 'var(--bg-surface)' }}>
        <ModalBody>
          {/* Drag handle */}
          <div className="flex justify-center pt-3 pb-2">
            <div className="w-9 h-1 rounded-full" style={{ backgroundColor: 'var(--border)' }} />
          </div>

          {/* File info header */}
          <div
            className="flex items-center gap-3 px-4 pb-3 mb-1"
            style={{ borderBottom: '1px solid var(--border)' }}
          >
            <div
              className="w-9 h-9 rounded-md flex items-center justify-center text-[10px] font-bold text-white"
              style={{
                backgroundColor: file.mime_type === 'application/pdf' ? '#dc3545' : '#28a745',
              }}
            >
              {ext}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                {file.title}
              </div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {formatSize(file.file_size)}
                {file.created_at && ` · ${new Date(file.created_at).toLocaleDateString()}`}
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="py-1">
            <button
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--bg-hover)]"
              onClick={handleDownload}
            >
              <Download size={18} style={{ color: 'var(--text-secondary)' }} />
              <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Download</span>
            </button>

            {isTablet && canViewMarkup && onOpenViewer && (
              <button
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--bg-hover)]"
                onClick={() => { onOpenViewer(file.id); onClose(); }}
              >
                <Eye size={18} style={{ color: 'var(--text-secondary)' }} />
                <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Open in Viewer</span>
              </button>
            )}

            {onMoveToFolder && (
              <button
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--bg-hover)]"
                onClick={() => { onMoveToFolder(); onClose(); }}
              >
                <FolderInput size={18} style={{ color: 'var(--text-secondary)' }} />
                <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Move to Folder…</span>
              </button>
            )}

            <button
              className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[var(--bg-hover)]"
              onClick={handleDelete}
            >
              <Trash2 size={18} style={{ color: '#ff6b6b' }} />
              <span className="text-sm" style={{ color: '#ff6b6b' }}>Delete</span>
            </button>
          </div>

          {/* Cancel */}
          <div className="px-4 pb-4 pt-1" style={{ borderTop: '1px solid var(--border)' }}>
            <Button
              size="sm"
              variant="flat"
              fullWidth
              onPress={onClose}
              className="text-[var(--text-secondary)]"
            >
              Cancel
            </Button>
          </div>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
